import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { QUERY_CONFIG } from "@/lib/queryConfig";
import type { CustomizationOption } from "../types";

const QUERY_KEY = ["product_customization_options"] as const;

/**
 * Fetch all configurable customization options, ordered by display_order.
 * Shared by the Product Configuration manager and the Create-Proof catalog
 * card quick-pick — same cache key so an edit in config reflects everywhere.
 */
export function useCustomizationOptionsQuery() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: fetchCustomizationOptions,
    ...QUERY_CONFIG.categories,
  });
}

useCustomizationOptionsQuery.QUERY_KEY = QUERY_KEY;

async function fetchCustomizationOptions(): Promise<CustomizationOption[]> {
  const { data, error } = await supabase
    .from("product_customization_options")
    .select("*")
    .order("display_order", { ascending: true });

  if (error) throw error;
  return (data ?? []) as CustomizationOption[];
}

/**
 * Options a product of the given tier may use:
 * Basic → only options flagged `available_for_basic`; Habits Exclusives → all options.
 *
 * Generic over the option shape so it serves both customization and decoration
 * options (and any future option list carrying `available_for_basic`).
 */
export function optionsForTier<T extends { available_for_basic: boolean }>(
  options: T[],
  tier: "basic" | "exclusive",
): T[] {
  return tier === "exclusive"
    ? options
    : options.filter((o) => o.available_for_basic);
}
